import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../auth";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PortalHeader } from "../components/PortalHeader";

export default function TeamPage() {
  const { API, authHeaders } = useAuth();
  const [staff, setStaff] = useState([]);
  const [trips, setTrips] = useState([]);
  const [form, setForm] = useState({ full_name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [assigning, setAssigning] = useState(null); // trip id currently editing

  const load = async () => {
    const s = (await axios.get(`${API}/admin/staff`, { headers: authHeaders })).data;
    setStaff(s);
    const t = (await axios.get(`${API}/trips`, { headers: authHeaders })).data;
    setTrips(t);
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await axios.post(`${API}/admin/staff`, form, { headers: authHeaders });
      toast.success("Invite sent");
      setForm({ full_name: "", email: "" });
      load();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const remove = async (uid) => {
    if (!confirm("Deactivate this team member?")) return;
    try {
      await axios.delete(`${API}/admin/staff/${uid}`, { headers: authHeaders });
      toast.success("Deactivated");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const toggleAssign = async (tripId, staffId, currentlyAssigned) => {
    const trip = trips.find((t) => t.id === tripId);
    const current = trip.assigned_staff_ids || [];
    const next = currentlyAssigned ? current.filter((id) => id !== staffId) : [...current, staffId];
    try {
      await axios.post(`${API}/admin/trips/${tripId}/assign-staff`,
        { staff_ids: next }, { headers: authHeaders });
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="min-h-screen" data-testid="team-page">
      <PortalHeader eyebrow="Team" backTo="/admin" />
      <div className="px-6 py-10 max-w-4xl mx-auto">
      <h1 className="font-display text-4xl">Team</h1>
      <hr className="tcm-gold-rule mb-8 mt-4" />

      <section className="mb-10">
        <p className="tcm-eyebrow mb-3">Invite an Ops Associate</p>
        <form onSubmit={invite} className="tcm-double-frame">
          <div className="tcm-inner p-5 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="tcm-eyebrow block mb-1">Full name</label>
                <input data-testid="staff-name" className="tcm-input" required
                       value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
              <div><label className="tcm-eyebrow block mb-1">Email</label>
                <input data-testid="staff-email" type="email" className="tcm-input" required
                       value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <button data-testid="staff-invite-submit" disabled={busy} className="tcm-btn-primary">
              {busy ? "Sending..." : "Send invitation"}
            </button>
          </div>
        </form>
      </section>

      <section className="mb-12">
        <p className="tcm-eyebrow mb-3">Team members</p>
        {staff.length === 0 && <p className="text-sm text-[color:var(--tcm-navy-70)]">No team members yet.</p>}
        <div className="space-y-3">
          {staff.map((u) => (
            <div key={u.id} className="border border-[#1E2D50] p-4 flex justify-between items-center"
                 data-testid={`staff-row-${u.id}`}>
              <div>
                <p className="font-display text-xl">{u.full_name}</p>
                <p className="tcm-eyebrow mt-1">{u.email} · {u.role} · {u.active ? "active" : "inactive"}</p>
              </div>
              {u.role !== "admin" && u.active && (
                <button data-testid={`del-staff-${u.id}`} onClick={() => remove(u.id)}
                        className="tcm-btn-outline">Deactivate</button>
              )}
            </div>
          ))}
        </div>
      </section>

      <section>
        <p className="tcm-eyebrow mb-3">Pin staff to trips</p>
        <p className="text-xs text-[color:var(--tcm-navy-70)] mb-4">
          When a trip has any pinned staff, only pinned staff (plus admins) may edit it. Trips with no pins remain open to all staff.
        </p>
        <div className="space-y-4">
          {trips.map((t) => {
            const assigned = t.assigned_staff_ids || [];
            const isOpen = assigning === t.id;
            return (
              <div key={t.id} className="tcm-double-frame" data-testid={`trip-assign-${t.id}`}>
                <div className="tcm-inner p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-display text-xl">{t.destination}</p>
                      <p className="tcm-eyebrow mt-1">{t.start_date} → {t.end_date}</p>
                    </div>
                    <button data-testid={`toggle-assign-${t.id}`} onClick={() => setAssigning(isOpen ? null : t.id)}
                            className="tcm-btn-outline">
                      {assigned.length === 0 ? "Open to all" : `${assigned.length} pinned`}
                    </button>
                  </div>
                  {isOpen && (
                    <div className="mt-4 border-t border-[color:var(--tcm-navy-15)] pt-4 space-y-2">
                      {staff.filter((s) => s.role === "staff" && s.active).map((s) => {
                        const on = assigned.includes(s.id);
                        return (
                          <label key={s.id} className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={on}
                                   data-testid={`assign-check-${t.id}-${s.id}`}
                                   onChange={() => toggleAssign(t.id, s.id, on)} />
                            <span>{s.full_name} — <span className="tcm-eyebrow">{s.email}</span></span>
                          </label>
                        );
                      })}
                      {staff.filter((s) => s.role === "staff" && s.active).length === 0 && (
                        <p className="text-xs text-[color:var(--tcm-navy-70)]">Invite an Ops Associate first.</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>
      </div>
    </div>
  );
}
